import { motion } from "framer-motion"
import { useNavigate } from "react-router-dom"
import { CheckCircle, MessageSquare, User, Workflow, Database, Sparkles, ArrowRight, Save } from "lucide-react"

const FinishAgentSetup = ({ agentDetails }) => {
  const navigate = useNavigate()

  const handleGoToAgents = () => {
    navigate("/ai-agents")
  }

  const handleSaveAgent = () => {
    const existingAgents = JSON.parse(localStorage.getItem("agents") || "[]")
    const newAgent = {
      id: existingAgents.length + 1,
      name: agentDetails.useCase,
      type: agentDetails.personality,
      description: `Channels: ${agentDetails.communicationChannels.join(", ")}, Flow: ${agentDetails.conversationFlow}, CRM: ${agentDetails.crmIntegration}`,
      stats: { callsThisMonth: "0", conversionRate: "0%" },
    }
    localStorage.setItem("agents", JSON.stringify([...existingAgents, newAgent]))
    navigate("/ai-agents")
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  }

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.4 },
    },
    hover: {
      scale: 1.02,
      transition: { duration: 0.2 },
    },
  }

  const agentSummaryItems = [
    {
      icon: MessageSquare,
      title: "Communication Channels",
      value: agentDetails.communicationChannels.join(", "),
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: Sparkles,
      title: "Use Case",
      value: agentDetails.useCase,
      color: "from-purple-500 to-pink-500",
    },
    {
      icon: User,
      title: "Personality",
      value: agentDetails.personality,
      color: "from-green-500 to-emerald-500",
    },
    {
      icon: Workflow,
      title: "Conversation Flow",
      value: agentDetails.conversationFlow,
      color: "from-orange-500 to-red-500",
    },
  ]

  return (
    <div className="min-h-[calc(100vh-100px)] bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex flex-col justify-center">
      <motion.div className="max-w-4xl mx-auto" variants={containerVariants} initial="hidden" animate="visible">
        {/* Header */}
        <motion.div className="text-center mb-6" variants={itemVariants}>
          <motion.div
            className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full mb-3 shadow-lg"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, type: "spring", bounce: 0.4 }}
          >
            <CheckCircle className="w-7 h-7 text-white" />
          </motion.div>

          <motion.h1
            className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-2"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Agent Setup Complete!
          </motion.h1>

          <motion.p className="text-base text-slate-600 max-w-xl mx-auto" variants={itemVariants}>
            Your AI agent has been configured and is ready to start engaging with your customers.
          </motion.p>
        </motion.div>

        {/* Agent Summary Cards */}
        <motion.div className="mb-6" variants={itemVariants}>
          <motion.h2 className="text-xl font-bold text-slate-800 mb-4 text-center" variants={itemVariants}>
            Agent Configuration Summary
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {agentSummaryItems.map((item, index) => (
              <motion.div
                key={item.title}
                className="relative group"
                variants={cardVariants}
                whileHover="hover"
                initial="hidden"
                animate="visible"
                transition={{ delay: index * 0.1 }}
              >
                <div
                  className="absolute inset-0 bg-gradient-to-r opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl blur-xl"
                  style={{
                    background: `linear-gradient(to right, ${item.color.split(" ")[1]}, ${item.color.split(" ")[3]})`,
                  }}
                />

                <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20 h-full">
                  <div
                    className={`inline-flex items-center justify-center w-8 h-8 bg-gradient-to-r ${item.color} rounded-xl mb-2 shadow-lg`}
                  >
                    <item.icon className="w-5 h-5 text-white" />
                  </div>

                  <h3 className="text-base font-semibold text-slate-800 mb-1">{item.title}</h3>

                  <p className="text-slate-600 leading-relaxed text-sm">{item.value}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div className="flex flex-col sm:flex-row gap-3 justify-center items-center" variants={itemVariants}>
          <motion.button
            className="group relative px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 min-w-[120px] justify-center text-sm"
            onClick={handleSaveAgent}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-700 to-purple-700 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <Save className="w-4 h-4 relative z-10" />
            <span className="relative z-10">Save Agent</span>
          </motion.button>

          <motion.button
            className="group relative px-4 py-2 bg-white text-slate-700 font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 min-w-[120px] justify-center border border-slate-200 text-sm"
            onClick={handleGoToAgents}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
          >
            <div className="absolute inset-0 bg-slate-50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <span className="relative z-10">Go to My Agents</span>
            <ArrowRight className="w-4 h-4 relative z-10 group-hover:translate-x-1 transition-transform duration-300" />
          </motion.button>
        </motion.div>
      </motion.div>
    </div>
  )
}

export default FinishAgentSetup
